/**
 * Global confirm-dialog registry — mirrors `useToast`. A single shared
 * ref holds the currently-pending confirmation (or null). The
 * `<ConfirmHost />` rendered once at the App root consumes it and shows
 * a branded, focus-managed, Esc-to-cancel dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Delete KB?", message: "…", danger: true }))
 *     await doDelete();
 *
 * Replaces native `window.confirm()` on destructive paths — unbranded
 * and not focus-managed.
 */
import { ref } from "vue";

export interface ConfirmOptions {
  /** Dialog heading. */
  title: string;
  /** Body text (plain string; rendered as-is). */
  message: string;
  /** Affirmative button label. */
  confirmLabel?: string;
  /** Cancel button label. */
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
}

export interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

const _pending = ref<PendingConfirm | null>(null);
let _nextId = 1;

function ask(opts: ConfirmOptions): Promise<boolean> {
  // Only one confirm at a time; if one is already open, resolve it
  // false first so we never strand a promise.
  if (_pending.value) {
    _pending.value.resolve(false);
    _pending.value = null;
  }
  return new Promise<boolean>((resolve) => {
    _pending.value = { id: _nextId++, ...opts, resolve };
  });
}

function _settle(ok: boolean): void {
  if (!_pending.value) return;
  _pending.value.resolve(ok);
  _pending.value = null;
}

/**
 * Compose-time accessor. Returns the shared pending ref (for the host)
 * plus the imperative `confirm(...)` and the settle handlers the host
 * wires to its buttons.
 */
export function useConfirm() {
  return {
    pending: _pending,
    confirm: ask,
    accept: () => _settle(true),
    cancel: () => _settle(false),
  };
}
